
import { useState, useRef, useEffect } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Maximize, Minimize, Film, Play } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export interface VideoProps {
  id: string;
  title: string;
  thumbnail: string;
  channelName: string;
  channelAvatar: string;
  views: number;
  published: string;
  duration: string;
}

export const VideoCard = ({
  id,
  title,
  thumbnail,
  channelName,
  channelAvatar,
  views,
  published,
  duration
}: VideoProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCinemaMode, setIsCinemaMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  const formatViews = (views: number) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M views`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K views`;
    }
    return `${views} views`;
  };

  // Fonction pour charger la vidéo directement avec l'URL YouTube
  const loadVideoDetails = async () => {
    setIsLoadingVideo(true);
    setError(null);
    
    try {
      // Utiliser directement l'ID YouTube sans passer par l'API
      let youtubeId = id;
      
      // Pour les IDs fictifs de notre base de données de test, utiliser des IDs YouTube réels
      if (id.startsWith('v') && id.length < 5) {
        const fallbackVideoIds = {
          v1: 'dQw4w9WgXcQ', // Rick Astley - Never Gonna Give You Up
          v2: 'hY7m5jjJ9mM', // CATS will make you LAUGH YOUR HEAD OFF
          v3: '9Auq9mYxFEE', // AI video
          v4: 'PFGwMiDCT_o', // AI video creation
          v5: 'ARJ8cAGm6JE', // AI tool video
          v6: 'Cj32fkx1FO0', // Future of AI
          v7: 'kCc8FmEb1nY', // AI that writes code
          v8: 'TxQRAPbBzxE', // AI image generators
        };
        
        youtubeId = fallbackVideoIds[id as keyof typeof fallbackVideoIds] || 'dQw4w9WgXcQ';
      }
      
      // Créer l'URL d'intégration YouTube directement
      setVideoUrl(`https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=1&showinfo=1&rel=0`);
      setIsPlaying(true);
      
      console.log('Vidéo chargée - ID affiché:', id, '- ID YouTube réel:', youtubeId);
    } catch (error) {
      console.error('Error loading video:', error);
      setError("Impossible de lire cette vidéo pour le moment.");
      toast({
        title: "Erreur de lecture",
        description: "Impossible de lire cette vidéo pour le moment.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingVideo(false);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      loadVideoDetails();
    }
  };

  const toggleCinemaMode = () => {
    setIsCinemaMode(!isCinemaMode);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const cardClasses = isCinemaMode 
    ? "fixed top-0 left-0 w-full h-full z-50 bg-black"
    : "group cursor-pointer";

  const contentClasses = isCinemaMode
    ? "container mx-auto h-full flex flex-col py-4"
    : "";
  
  return (
    <div 
      className={cardClasses}
      ref={containerRef}
    >
      <div className={contentClasses}>
        <div 
          className={`relative ${isCinemaMode ? 'flex-1 flex flex-col' : ''}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className={`relative ${isCinemaMode ? 'flex-1' : 'aspect-video'} rounded-xl overflow-hidden bg-gray-200 mb-3`}>
            {!isPlaying ? (
              <>
                {!isLoaded && !isLoadingVideo && (
                  <div className="absolute inset-0 shimmer"></div>
                )}
                {isLoadingVideo ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-60">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-youtube-red"></div>
                  </div>
                ) : (
                  <>
                    <img
                      src={thumbnail}
                      alt={title}
                      className={`w-full h-full object-cover transition-all duration-300 ${
                        isHovered ? 'scale-105' : 'scale-100'
                      } ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setIsLoaded(true)}
                      onClick={togglePlay}
                    />
                    <div 
                      className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={togglePlay}
                    >
                      <div className="w-16 h-16 bg-youtube-red rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                        <Play className="h-8 w-8 text-white fill-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1 py-0.5 rounded">
                      {duration}
                    </div>
                  </>
                )}
              </>
            ) : error ? (
              <div className="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-10">
                <Alert variant="destructive" className="max-w-md">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Erreur de lecture</AlertTitle>
                  <AlertDescription>
                    Impossible de lire cette vidéo pour le moment.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <AspectRatio ratio={16 / 9} className={`bg-black ${isCinemaMode ? 'h-full' : ''}`}>
                <iframe
                  ref={playerRef}
                  width="100%"
                  height="100%"
                  src={videoUrl}
                  title={title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </AspectRatio>
            )}
          </div>
          
          {(!isCinemaMode || !isPlaying) && (
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src={channelAvatar} 
                  alt={channelName}
                  className="w-full h-full object-cover" 
                />
              </div>
              
              <div className="flex-1">
                <h3 className="font-medium text-sm sm:text-base line-clamp-2 group-hover:text-youtube-red transition-colors">
                  {title}
                </h3>
                <p className="text-youtube-gray-dark text-sm mt-1">
                  {channelName}
                </p>
                <div className="text-youtube-gray-dark text-sm flex items-center">
                  <span>{formatViews(views)}</span>
                  <span className="mx-1">•</span>
                  <span>{published}</span>
                </div>
              </div>
            </div>
          )}
          
          {isCinemaMode && isPlaying && (
            <div className="mt-4">
              <Button variant="outline" onClick={toggleCinemaMode}>Quitter le mode cinéma</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
